import {
	type AdminExportUsersData,
	type AdminListUsersData,
	type AdminUserListItem,
	adminExportUsers
} from '@repo/sdk/gen'
import { zAdminUserListItem } from '@repo/sdk/zod'
import { Avatar, AvatarFallback, AvatarImage } from '@repo/ui/avatar'
import { Badge } from '@repo/ui/badge'
import { DataTableColumnSettings } from '@repo/ui/components/data-table/column-settings'
import {
	DataTable,
	useDataTableColumnState
} from '@repo/ui/components/data-table/data-table'
import type { DataTableColumn } from '@repo/ui/components/data-table/types'
import { ListInputSearch } from '@repo/ui/components/list-input-search'
import { ListLayout } from '@repo/ui/components/list-layout'
import { formatLocaleDate } from '@repo/ui/dates'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger
} from '@repo/ui/tooltip'
import { formatError, getInitials } from '@repo/ui/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
	createFileRoute,
	Link,
	stripSearchParams,
	useNavigate
} from '@tanstack/react-router'
import { Mail } from 'lucide-react'
import type { ReactNode } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'
import { useTranslation } from '@/lib/i18n'
import { TableExportMenu } from '@/routes/(private)/-components/table-export-menu'
import { downloadExportResult } from '@/routes/(private)/-lib/export-download'
import { ReleaseTaxCodeDialog } from '@/routes/(private)/users/-components/release-tax-code-dialog'
import { UserActionsDropdown } from '@/routes/(private)/users/-components/user-actions-dropdown'
import { UsersFilters } from '@/routes/(private)/users/-components/users-filters'
import { usersListQueryOptions } from '@/routes/(private)/users/-query/query-options'
import { usePageSize } from '@/stores/use-page-size'

type SortBody = NonNullable<AdminListUsersData['body']['sort']>
type SortBy = NonNullable<SortBody['by']>
type SortDirection = NonNullable<SortBody['direction']>

const SORT_BY_VALUES: readonly SortBy[] = ['createdAt', 'name'] as const
const SORT_DIRECTION_VALUES: readonly SortDirection[] = ['asc', 'desc'] as const

const SIGNUP_SOURCE_ICONS = {
	email: <Mail />
} satisfies Record<AdminUserListItem['signupSource'], ReactNode>
const EMPTY_USERS: AdminUserListItem[] = []

const searchSchema = z.object({
	emailVerified: z.boolean().optional(),
	status: z.array(zAdminUserListItem.shape.status).optional(),
	createdFrom: z.iso.datetime().optional(),
	createdTo: z.iso.datetime().optional(),
	search: z.string().trim().default(''),
	by: z.enum(SORT_BY_VALUES).optional(),
	direction: z.enum(SORT_DIRECTION_VALUES).optional(),
	page: z.number().int().min(1).default(1)
})

export const Route = createFileRoute('/(private)/users/')({
	component: AdminUsers,
	validateSearch: searchSchema,
	search: {
		middlewares: [stripSearchParams(searchSchema.parse({}))]
	}
})

function AdminUsers() {
	const { t } = useTranslation()
	const navigate = useNavigate({ from: Route.fullPath })
	const params = Route.useSearch()
	const pageSize = usePageSize((s) => s.pageSize)
	const setPageSize = usePageSize((s) => s.setPageSize)

	const columns: DataTableColumn<AdminUserListItem, SortBy>[] = [
		{
			id: 'name',
			header: t('common:name'),
			pinned: 'left',
			sortKey: 'name',
			// A student has no name until the application gives one: the email stands in.
			cell: (dataItem) => {
				const label = dataItem.name || dataItem.email || ''
				return (
					<Link
						className='group flex min-w-0 items-center gap-2'
						params={{ userId: dataItem.id }}
						to='/users/$userId'
					>
						<Avatar size='sm'>
							{dataItem.image?.url ? (
								<AvatarImage
									alt={label}
									src={dataItem.image.url}
								/>
							) : null}
							<AvatarFallback>
								{getInitials(label)}
							</AvatarFallback>
						</Avatar>
						<span className='truncate group-hover:underline'>
							{label}
						</span>
					</Link>
				)
			},
			minWidth: 240,
			flex: 1
		},
		{
			id: 'email',
			header: t('common:email'),
			cell: (dataItem) => dataItem.email,
			minWidth: 180,
			flex: 2
		},
		{
			id: 'status',
			header: t('admin-users:columns.status'),
			cell: (dataItem) => {
				if (dataItem.status === 'deleted') {
					return (
						<Badge variant='neutral'>{t('common:deleted')}</Badge>
					)
				}

				if (dataItem.status === 'banned') {
					return (
						<Badge
							variant={
								dataItem.banExpires
									? 'attention'
									: 'destructive'
							}
						>
							{dataItem.banExpires
								? t('admin-users:columns.ban_temporary')
								: t('admin-users:columns.ban_permanent')}
						</Badge>
					)
				}

				return <Badge variant='success'>{t('common:active')}</Badge>
			},
			minWidth: 130,
			flex: 1
		},
		{
			id: 'banReason',
			header: t('admin-users:columns.ban_reason'),
			cell: (dataItem) =>
				dataItem.status === 'banned'
					? (dataItem.banReason ??
						t('admin-users:columns.no_ban_reason'))
					: '-',
			minWidth: 180,
			flex: 2
		},
		{
			id: 'banExpires',
			header: t('admin-users:columns.ban_expires'),
			cell: (dataItem) =>
				dataItem.status === 'banned' && dataItem.banExpires
					? formatLocaleDate(dataItem.banExpires, 'P HH:mm:ss')
					: '-',
			minWidth: 160,
			flex: 1
		},
		{
			id: 'emailVerified',
			header: t('admin-users:columns.email_verified'),
			cell: (dataItem) =>
				dataItem.emailVerified ? (
					<TooltipProvider delay={300}>
						<Tooltip>
							<TooltipTrigger render={<span />}>
								<Badge variant='success'>
									{t('common:verified')}
								</Badge>
							</TooltipTrigger>
							{dataItem.emailVerifiedAt ? (
								<TooltipContent>
									{t('common:email_verified_on', {
										date: formatLocaleDate(
											dataItem.emailVerifiedAt,
											'P HH:mm:ss'
										)
									})}
								</TooltipContent>
							) : null}
						</Tooltip>
					</TooltipProvider>
				) : (
					<Badge variant='warning'>{t('common:unverified')}</Badge>
				),
			minWidth: 110,
			flex: 1
		},
		{
			id: 'signupSource',
			header: t('admin-users:columns.signup_source'),
			cell: (dataItem) => (
				<Badge variant='outline'>
					{SIGNUP_SOURCE_ICONS[dataItem.signupSource]}
					{t(`admin-users:signup_source.${dataItem.signupSource}`)}
				</Badge>
			),
			minWidth: 120,
			flex: 1
		},
		{
			id: 'createdAt',
			header: t('common:created_at'),
			sortKey: 'createdAt',
			cell: (dataItem) =>
				formatLocaleDate(dataItem.createdAt, 'P HH:mm:ss'),
			minWidth: 160,
			flex: 1
		},
		{
			id: 'actions',
			header: '',
			label: t('admin-users:columns.actions'),
			pinned: 'right',
			hideable: false,
			manageable: false,
			pinnable: false,
			enableResizing: false,
			cell: (dataItem) => (
				<div className='flex w-full justify-end'>
					<UserActionsDropdown user={dataItem} />
				</div>
			),
			width: 64,
			minWidth: 64,
			maxWidth: 64
		}
	]

	const columnsState = useDataTableColumnState(columns)

	const filters = {
		search: params.search || undefined,
		status: params.status,
		emailVerified: params.emailVerified,
		createdAt:
			params.createdFrom || params.createdTo
				? { from: params.createdFrom, to: params.createdTo }
				: undefined
	}
	const sort =
		params.by === undefined
			? undefined
			: { by: params.by, direction: params.direction ?? 'asc' }

	const query = useQuery(
		usersListQueryOptions({
			page: params.page,
			limit: pageSize,
			filters,
			sort
		})
	)

	const exportUsers = useMutation({
		mutationFn: async (body: AdminExportUsersData['body']) =>
			downloadExportResult(
				await adminExportUsers({ body }),
				`admin-users.${body.format}`
			),
		onSuccess: (result) => {
			if (result === 'downloaded') {
				toast.success(t('admin-exports:dialog.downloaded'))
				return
			}

			toast.success(t('admin-exports:dialog.started'))
		},
		onError: (error) => toast.error(formatError(error))
	})

	const handleExport = (format: AdminExportUsersData['body']['format']) => {
		exportUsers.mutate({
			format,
			mode: 'auto',
			filters,
			sort
		})
	}

	return (
		<ListLayout
			controlsSlot={
				<>
					<ListInputSearch
						placeholder={t('admin-users:search_placeholder')}
						resetKeys={['page']}
					/>
					<UsersFilters />
					<DataTableColumnSettings
						columns={columns}
						columnsState={columnsState}
					/>
				</>
			}
			headEndSlot={
				<div className='ml-auto flex items-center gap-2'>
					<ReleaseTaxCodeDialog />
					<TableExportMenu
						onSelectFormat={handleExport}
						pending={exportUsers.isPending}
					/>
				</div>
			}
			listSlot={
				<DataTable
					columns={columns}
					columnsState={columnsState}
					data={query.data?.data ?? EMPTY_USERS}
					onRowClick={(row) =>
						navigate({
							params: { userId: row.id },
							to: '/users/$userId'
						})
					}
					pagination={{
						pageIndex: params.page - 1,
						pageCount: query.data?.meta.totalPages ?? 1,
						onPageChange: (next) =>
							navigate({
								search: (prev) => ({ ...prev, page: next + 1 }),
								replace: true
							}),
						pageSize,
						onPageSizeChange: (next) => {
							setPageSize(next)
							navigate({
								search: (prev) => ({ ...prev, page: 1 }),
								replace: true
							})
						}
					}}
					sort={{
						state: { by: params.by, direction: params.direction },
						onSortChange: (next) =>
							navigate({
								search: (prev) => ({
									...prev,
									by: next.by,
									direction: next.direction,
									page: 1
								}),
								replace: true
							})
					}}
					state={{
						loading: query.isPending || query.isPlaceholderData,
						error: query.isError
					}}
				/>
			}
			title={t('admin-users:page_title')}
		/>
	)
}
